import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle, Calendar, Info } from "lucide-react";
import axios from "axios";
import OTPModal from "./OTPModal";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { useLang } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function PreOrderModal({ mushroom, onClose }) {
  const rawFrom = mushroom.preOrderFrom;
  const rawUntil = mushroom.preOrderUntil;
  const hasDateRange = rawFrom && rawUntil;
  const dateMin = hasDateRange ? (rawFrom <= rawUntil ? rawFrom : rawUntil) : null;
  const dateMax = hasDateRange ? (rawFrom <= rawUntil ? rawUntil : rawFrom) : null;
  const isInquiry = !hasDateRange;

  const [form, setForm] = useState({ customerName: "", phone: "", address: "", qty: 1, preferredDate: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [waUrl, setWaUrl] = useState(null);
  const [showOTP, setShowOTP] = useState(false);

  const { isVerified, setVerified } = useCustomerAuth();
  const { t } = useLang();
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const buildWAMessage = () => {
    if (isInquiry) {
      return `Hello PIIKK Agro Farm Fresh Mushrooms!\n\nI'm interested in *${mushroom.name}* and would like to enquire about availability.\n\nName: ${form.customerName}\nPhone: ${form.phone}\nAddress: ${form.address}\nEstimated Qty: ${form.qty} x ${mushroom.uom || "250g"}${form.notes ? `\nMessage: ${form.notes}` : ""}\n\nKindly let me know when it will be available and the expected delivery date. Thank you!`;
    }
    return `Hello PIIKK Agro Farm Fresh Mushrooms!\n\nI'd like to place a *Pre-Order*:\n\n  - ${mushroom.name} (${mushroom.uom || "250g"}) x${form.qty}\n  Preferred Delivery: ${form.preferredDate ? fmt(form.preferredDate) : "As available"}\n\nName: ${form.customerName}\nPhone: ${form.phone}\nAddress: ${form.address}${form.notes ? `\nNotes: ${form.notes}` : ""}\n\nKindly acknowledge this pre-order and confirm the delivery date and time. Thank you!`;
  };

  const doSubmit = async () => {
    setLoading(true);
    const msg = buildWAMessage();
    const url = `https://wa.me/916362516143?text=${encodeURIComponent(msg)}`;
    try {
      await axios.post(`${API}/preorders`, {
        mushroomId: mushroom.id,
        mushroomName: mushroom.name,
        customerName: form.customerName,
        phone: form.phone,
        address: form.address,
        qty: parseInt(form.qty),
        notes: [form.preferredDate ? `Preferred date: ${form.preferredDate}` : "", form.notes].filter(Boolean).join(" | ") || null
      });
    } catch (err) {
      console.error("Pre-order save failed:", err);
    } finally {
      setLoading(false);
    }
    setWaUrl(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isVerified(form.phone)) {
      setShowOTP(true);
      return;
    }
    doSubmit();
  };

  const inputCls = "w-full px-4 py-2.5 rounded-xl border text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D97A46]/30 transition-colors";
  const inputStyle = { backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)', color: 'var(--c-text)' };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[80] flex items-center justify-center p-4"
        onClick={e => e.target === e.currentTarget && onClose()}
        data-testid="preorder-modal-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full max-w-md rounded-2xl shadow-xl flex flex-col border"
          style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)', maxHeight: '90vh' }}
          data-testid="preorder-modal"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--c-border)' }}>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" style={{ color: '#D97A46' }} />
              <h2 className="font-serif text-xl font-bold" style={{ color: 'var(--c-text)' }}>
                {isInquiry ? t('preorder.enquiryTitle') : t('preorder.title')}
              </h2>
            </div>
            <button onClick={onClose} data-testid="close-preorder-modal"
              className="p-1.5 rounded-full transition-colors hover:bg-gray-100" style={{ color: 'var(--c-muted)' }}>
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="overflow-y-auto flex-1">
          {!waUrl ? (
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-xl border" style={{ backgroundColor: 'var(--c-bg)', borderColor: 'var(--c-border)' }}>
                <img src={mushroom.image} alt={mushroom.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                <div>
                  <p className="font-semibold text-sm" style={{ color: 'var(--c-text)' }}>{mushroom.name}</p>
                  <p className="text-xs" style={{ color: 'var(--c-muted)' }}>{mushroom.uom || "250g"} · Rs.{mushroom.price}</p>
                  {hasDateRange ? (
                    <p className="text-xs text-amber-600 mt-0.5">Pre-order: {fmt(dateMin)} – {fmt(dateMax)}</p>
                  ) : (
                    <p className="text-xs text-amber-600 mt-0.5">{t('preorder.comingSoon')}</p>
                  )}
                </div>
              </div>

              {isInquiry && (
                <div className="flex items-start gap-2.5 p-3 rounded-xl border border-amber-200 bg-amber-50 text-xs text-amber-700">
                  <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <p>{t('preorder.enquiryInfo')}</p>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--c-muted)' }}>{t('preorder.name')} *</label>
                <input className={inputCls} style={inputStyle} required value={form.customerName}
                  onChange={e => set("customerName", e.target.value)} placeholder={t('preorder.namePlaceholder')} data-testid="preorder-name-input" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--c-muted)' }}>{t('preorder.phone')} *</label>
                <input className={inputCls} style={inputStyle} required type="tel" maxLength={10}
                  value={form.phone} onChange={e => set("phone", e.target.value.replace(/\D/g, ""))}
                  placeholder={t('preorder.phonePlaceholder')} data-testid="preorder-phone-input" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--c-muted)' }}>{t('preorder.address')} *</label>
                <textarea className={inputCls} style={inputStyle} required rows={2} value={form.address}
                  onChange={e => set("address", e.target.value)} placeholder={t('preorder.addressPlaceholder')}
                  data-testid="preorder-address-input" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--c-muted)' }}>{t('preorder.qty')} *</label>
                <input className={inputCls} style={inputStyle} required type="number" min="1" max="100"
                  value={form.qty} onChange={e => set("qty", e.target.value)} data-testid="preorder-qty-input" />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--c-muted)' }}>
                  {hasDateRange ? t('preorder.date') : t('preorder.dateUnavailable')} {hasDateRange ? '*' : ''}
                </label>
                <input
                  className={`${inputCls} ${isInquiry ? "opacity-40 cursor-not-allowed" : ""}`}
                  style={{ ...inputStyle, colorScheme: 'light' }}
                  type="date" disabled={isInquiry} required={hasDateRange}
                  min={dateMin || undefined} max={dateMax || undefined}
                  value={form.preferredDate} onChange={e => set("preferredDate", e.target.value)}
                  data-testid="preorder-date-input"
                />
                {hasDateRange && <p className="text-xs mt-1" style={{ color: 'var(--c-muted)' }}>{t('preorder.chooseBetween', { from: fmt(dateMin), until: fmt(dateMax) })}</p>}
                {isInquiry && <p className="text-xs text-amber-600 mt-1">{t('preorder.noDatesSet')}</p>}
              </div>

              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--c-muted)' }}>{isInquiry ? t('preorder.enquiryNotes') : t('preorder.notes')}</label>
                <textarea className={inputCls} style={inputStyle} rows={2} value={form.notes}
                  onChange={e => set("notes", e.target.value)}
                  placeholder={isInquiry ? t('preorder.enquiryNotesPh') : t('preorder.notesPh')} />
              </div>

              <button type="submit" disabled={loading} data-testid="submit-preorder-btn"
                className="w-full py-3.5 rounded-full font-semibold text-white flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 hover:shadow-md disabled:opacity-60"
                style={{ backgroundColor: '#25D366' }}>
                <MessageCircle className="w-5 h-5" />
                {loading ? t('preorder.loading') : (isInquiry ? t('preorder.enquirySubmit') : t('preorder.submit'))}
              </button>
              <p className="text-center text-xs pb-2" style={{ color: 'var(--c-muted)' }}>
                {isVerified(form.phone) ? t('preorder.verifiedNote') : t('preorder.verifyNote')}
              </p>
            </form>
          ) : (
            <div className="p-8 text-center" data-testid="preorder-success">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="font-serif text-xl font-bold mb-2" style={{ color: 'var(--c-text)' }}>
                {isInquiry ? t('preorder.enquirySuccessTitle') : t('preorder.successTitle')}
              </h3>
              <p className="text-sm mb-6" style={{ color: 'var(--c-muted)' }}>
                {isInquiry
                  ? t('preorder.enquirySuccessMsg', { name: mushroom.name })
                  : t('preorder.successMsg', { name: mushroom.name })}
              </p>
              <a href={waUrl} target="_blank" rel="noreferrer" data-testid="open-whatsapp-btn"
                className="w-full mb-3 py-3.5 rounded-full font-semibold text-white flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 hover:shadow-md"
                style={{ backgroundColor: '#25D366', display: 'flex' }}>
                <MessageCircle className="w-5 h-5" /> {t('preorder.whatsapp')}
              </a>
              <button onClick={onClose}
                className="w-full px-6 py-2.5 rounded-full border text-sm font-medium transition-all mt-2 hover:bg-gray-50"
                style={{ borderColor: 'var(--c-border)', color: 'var(--c-muted)' }}>
                {t('preorder.close')}
              </button>
            </div>
          )}
          </div>
        </motion.div>
      </motion.div>

      {showOTP && (
        <OTPModal
          phone={form.phone}
          name={form.customerName}
          onVerified={(phone, name, token) => {
            setVerified(phone, name, token);
            setShowOTP(false);
            doSubmit();
          }}
          onClose={() => setShowOTP(false)}
        />
      )}
    </AnimatePresence>
  );
}
